import { CompanySettingsTypeID } from '@setvi/shared/services/react-query/query/user/types';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { useRealmContext } from 'Providers/MongoRealmProvider/context';
import { MONGO_REALM_COLLECTIONS } from 'Services/MongoRealm';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { CustomerPreferencesInterface } from '../interfaces';

interface useCustomerPreferencesProps {
  limit?: number;
}

export const useCustomerPreferences = ({
  limit = 10
}: useCustomerPreferencesProps = {}) => {
  const { companyData } = useAppContext();
  const { client } = useRealmContext();
  const [customers, setCustomers] = useState<CustomerPreferencesInterface[]>(
    []
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLastPage, setIsLastPage] = useState<boolean>(false);
  const [skipValue, setSkipValue] = useState<number>(0);
  const { enqueueSnackbar } = useSnackbar();

  const searchCustomerPreferences = useCallback(
    async (query: string, skip = 0) => {
      setIsLoading(true);
      setSearchQuery(query);
      try {
        const response: CustomerPreferencesInterface[] = await client
          .db(
            companyData.Settings[CompanySettingsTypeID.AtlasMongoProductDBName]
              ?.Value
          )
          .collection(MONGO_REALM_COLLECTIONS.CUSTOMERS)
          .aggregate([
            {
              $match: {
                $or: [
                  { vetName: { $regex: query, $options: 'i' } },
                  { cityState: { $regex: query, $options: 'i' } }
                ]
              }
            },
            { $skip: skip },
            {
              $limit: limit
            }
          ]);

        setCustomers(prevCustomers => [...prevCustomers, ...response]);
        setIsLastPage(response?.length < limit);
        setSkipValue(prevSkip => prevSkip + limit);

        return response;
      } catch (e) {
        enqueueSnackbar('Error occured while fetching prefrence data', {
          variant: 'error'
        });
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [client, enqueueSnackbar, limit, companyData]
  );

  const fetchMoreSearchResults = () => {
    if (isLastPage) return;
    searchCustomerPreferences(searchQuery, skipValue);
  };

  useEffect(() => {
    setSkipValue(0);
    setCustomers([]);
  }, [searchQuery]);

  return {
    customers,
    isLoading,
    searchCustomerPreferences,
    fetchMoreSearchResults,
    searchQuery
  };
};
